/**
 * web/src/components/gates/OpsTemplatePicker.tsx
 *
 * Section picker for the ops next-steps email gate (step_08). The email body is
 * one section from the Exhibit Next Steps Doc — that doc carries every variant
 * the operator might send (booth footprints AND the "no exhibit space" cases),
 * so the picker is single-select (radio) over one group. The auto-selected
 * default is the booth footprint from the Tracker.
 *
 * Picking a booth recomposes the body IMMEDIATELY — clicking a radio fires the
 * regenerate (workflow stays paused, no advance); there is no separate "apply"
 * step. (Reviewer feedback 2026-06: clicking a size then hunting for a separate
 * button was confusing, and people thought picking did nothing.)
 *
 * Renders above EmailPreview on the ops gate only (keyed off
 * `gateData.context.templatePicker`); every other email gate is untouched.
 *
 * Selection state is LOCAL: `selected` tracks the pick in flight while the
 * server recomposes; `picker.selected` describes what produced the body
 * currently shown. The parent remounts this component (via a `key` derived from
 * the composed selection) once a regenerate persists, re-converging the two.
 */
import { useMemo, useState } from 'react';
import { Button } from '../shared/Button';
import { Badge, type BadgeProps } from '../shared/Badge';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';
import type { OpsTemplatePicker as OpsTemplatePickerData } from '../../api/types';

/** Maps a non-ok ingest status to a Badge variant + label (ok → no badge). */
function ingestStatusBadge(status: string): { variant: BadgeProps['variant']; label: string } | null {
  switch (status) {
    case 'review':
      return { variant: 'waiting', label: 'needs review' };
    case 'stale':
      return { variant: 'waiting', label: 'stale' };
    case 'error':
      return { variant: 'failed', label: 'error' };
    default:
      return null;
  }
}

/** Public props for OpsTemplatePicker. */
export interface OpsTemplatePickerProps {
  /** The picker state from `gateData.context.templatePicker` (or regenerate response). */
  picker: OpsTemplatePickerData;
  /** Read-only view (the viewer can't approve this gate) — controls disabled. */
  disabled?: boolean;
  /** True while a regenerate request is in flight. */
  isRegenerating?: boolean;
  /** Fired when the reviewer applies a new pick. */
  onRegenerate: (selected: string[]) => void;
  className?: string;
}

/**
 * Single-select section radio + Regenerate button for the ops gate. Pure
 * presentation over local edit state; the parent owns the regenerate mutation
 * and reseeds the email editor from its response.
 *
 * @sideEffects none beyond local state; calls `onRegenerate` on Regenerate click
 * @idempotent yes
 */
export function OpsTemplatePicker({
  picker,
  disabled = false,
  isRegenerating = false,
  onRegenerate,
  className,
}: OpsTemplatePickerProps) {
  // Local edit state — the sectionKey the reviewer wants NEXT, vs
  // `picker.selected[0]` which is what produced the body currently shown.
  // Seeded from the composed pick on mount. The parent remounts this
  // component (via a `key` derived from the composed selection) once a
  // regenerate persists, so a successful apply re-seeds without a sync
  // effect; in-progress toggles persist because the key is stable until the
  // new pick is composed.
  const [selected, setSelected] = useState<string | null>(picker.selected[0] ?? null);

  // sectionKey → label, for the collapsed summary line.
  const labelByKey = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of picker.groups) for (const s of g.sections) m.set(s.sectionKey, s.label);
    return m;
  }, [picker.groups]);

  // The picker opens COLLAPSED to a one-line summary of the chosen email when a
  // pick already exists (the common case — the booth was auto-selected from
  // the Tracker); the reviewer clicks "Change" to expand the full list. With
  // nothing selected there's nothing to summarise, so it opens expanded so the
  // reviewer can pick. A successful regenerate remounts this component (parent
  // `key`), re-collapsing onto the freshly composed pick.
  const composedSelectionExists =
    picker.selected.length > 0 || picker.selectedLabels.length > 0;
  const [expanded, setExpanded] = useState<boolean>(!composedSelectionExists);

  // Summary label for the collapsed row: composed pick → fallback to the
  // composed label echo (rationale-only case where no key was picked).
  const summaryLabel =
    picker.selected[0] != null
      ? labelByKey.get(picker.selected[0]) ?? picker.selected[0]
      : picker.selectedLabels[0] ?? null;

  const totalSections = picker.groups.reduce((n, g) => n + g.sections.length, 0);

  // Picking a booth recomposes the body immediately — no separate "apply".
  // Guard against re-firing for the pick that already produced the body, and
  // against double-firing while a compose is in flight.
  const composedKey = picker.selected[0] ?? null;
  const pick = (sectionKey: string) => {
    setSelected(sectionKey);
    if (disabled || isRegenerating || sectionKey === composedKey) return;
    onRegenerate([sectionKey]);
  };

  // The auto-suggest rationale is only meaningful on the initial open (or when
  // distinct from a manual pick) — suppress it for manual selections.
  const showSuggestion =
    picker.suggested.length > 0 &&
    picker.suggestReasoning.trim() !== '' &&
    picker.suggestReasoning !== 'manual selection';

  return (
    <div className={cn('rounded-lg border border-border bg-bg-soft', className)}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
        <Icon name="mail" size={14} className="text-text-muted shrink-0" />
        <span className="text-[13px] font-semibold text-text">Email section</span>
        <div className="flex-1" />
        <span className="text-[12px] text-text-muted">
          {selected ? 1 : 0} of {totalSections} selected
        </span>
      </div>

      {!expanded ? (
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="min-w-0 flex-1 flex flex-col gap-0.5">
            {summaryLabel ? (
              <span className="text-[13px] text-text truncate">
                <span className="text-text-muted">Email: </span>
                {summaryLabel}
              </span>
            ) : (
              <span className="text-[13px] text-text-muted">No section selected yet</span>
            )}
          </div>
          {!disabled && (
            <Button
              type="button"
              size="sm"
              variant="default"
              onClick={() => setExpanded(true)}
              className="shrink-0"
            >
              <Icon name="chevron-down" size={12} />
              Change
            </Button>
          )}
        </div>
      ) : (
        <div className="px-4 py-3 flex flex-col gap-3">
          {picker.storeEmpty ? (
            <div
              role="alert"
              className="flex items-start gap-2.5 px-3 py-2.5 rounded-md bg-waiting-soft border border-waiting-border text-waiting text-[12px] leading-[1.55]"
            >
              <Icon name="alert" size={14} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold mb-0.5">No template sections stored yet</div>
                An admin needs to run <strong>Sync templates</strong> in this event&apos;s
                settings before the email can be composed from a section. You can still edit the
                body manually below.
              </div>
            </div>
          ) : (
            <>
              {showSuggestion && (
                <div className="flex items-start gap-2 text-[12px] text-text-muted leading-[1.55]">
                  <Icon name="info" size={12} className="mt-0.5 shrink-0" />
                  <span>{picker.suggestReasoning}</span>
                </div>
              )}

              {picker.groups.map((group) => {
                const badge = ingestStatusBadge(group.status);
                return (
                  <fieldset
                    key={group.docKey}
                    disabled={disabled}
                    className="m-0 p-0 border-0 min-w-0"
                  >
                    <legend className="flex items-center gap-2 mb-1.5 p-0 text-[11px] font-semibold uppercase tracking-wider text-text-subtle">
                      {group.label}
                      {badge && (
                        <Badge variant={badge.variant} title="Ingest status of the source Doc">
                          {badge.label}
                        </Badge>
                      )}
                    </legend>
                    {group.sections.length === 0 ? (
                      <p className="text-[12px] text-text-muted pl-0.5">
                        No sections in this doc.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {group.sections.map((s) => (
                          <label
                            key={s.sectionKey}
                            className={cn(
                              'flex items-start gap-2.5 px-2 py-1.5 rounded-md text-[13px]',
                              !disabled && 'hover:bg-bg-muted cursor-pointer',
                            )}
                          >
                            <input
                              type="radio"
                              name="ops-section"
                              className="w-4 h-4 accent-accent mt-0.5 shrink-0"
                              checked={selected === s.sectionKey}
                              onChange={() => pick(s.sectionKey)}
                              disabled={disabled || isRegenerating}
                            />
                            <span className="min-w-0">
                              <span className="text-text">{s.label}</span>
                              {s.subject && (
                                <span className="block text-[11px] text-text-muted truncate">
                                  {s.subject}
                                </span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </fieldset>
                );
              })}
            </>
          )}

          <div className="flex items-center gap-3 border-t border-border pt-3 flex-wrap">
            {isRegenerating ? (
              <span className="flex items-center gap-1.5 text-[12px] text-text-muted">
                <Icon name="retry" size={12} className="animate-spin" />
                Composing the email…
              </span>
            ) : (
              !disabled && (
                <span className="text-[12px] text-text-muted">
                  {selected
                    ? 'Pick a different size to recompose the email.'
                    : 'Pick a section to compose the email.'}
                </span>
              )
            )}
            {composedSelectionExists && !isRegenerating && (
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="ml-auto text-[12px] text-text-muted hover:text-text underline-offset-2 hover:underline"
              >
                Collapse
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
